// Package Imports
import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

// Core Imports
import { queryClient } from '@/core/lib/query-client'
import { routeHasPermission } from '@/core/utils/auth-utils'
import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'

// Module Imports
import {
  allPermissionsQueryOptions,
  roleDetailQueryOptions,
  roleListQueryOptions,
} from '@/modules/role/role.hooks'

export const rolesRoute = createRoute({
  path: '/roles',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const rolesListRoute = createRoute({
  getParentRoute: () => rolesRoute,
  path: '/',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'roles.list',
      'Rol listesine erişim izniniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Rol Listesi' }],
  }),
  loader: async () => {
    await queryClient.ensureQueryData(roleListQueryOptions())
  },
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/role/pages/list-role.page')
  ),
})

export const rolesCreateRoute = createRoute({
  getParentRoute: () => rolesRoute,
  path: 'create',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'roles.create',
      'Rol oluşturmak için yetkiniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Rol Oluştur' }],
  }),
  loader: async () => {
    await queryClient.ensureQueryData(allPermissionsQueryOptions())
  },
  component: lazyRouteComponent(
    () => import('@/modules/role/pages/create-role.page')
  ),
})

export const rolesDetailRoute = createRoute({
  getParentRoute: () => rolesRoute,
  path: '/$id',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'roles.detail',
      'Rol detayına erişim izniniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Rol Detayı' }],
  }),
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(roleDetailQueryOptions(id)),
      queryClient.ensureQueryData(allPermissionsQueryOptions()),
    ])
  },
  component: lazyRouteComponent(
    () => import('@/modules/role/pages/role-detail.page')
  ),
})

export const rolesEditRoute = createRoute({
  getParentRoute: () => rolesRoute,
  path: '/$id/edit',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'roles.edit',
      'Rol düzenlemek için yetkiniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Rol Düzenle' }],
  }),
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(roleDetailQueryOptions(id)),
      queryClient.ensureQueryData(allPermissionsQueryOptions()),
    ])
  },
  component: lazyRouteComponent(
    () => import('@/modules/role/pages/edit-role.page')
  ),
})
